import {
  deleteBoardingPass,
  deleteTripClimateDay,
  deleteTripEvent,
  deleteTripExperience,
  deleteTripHotel,
  deleteTripItineraryItem,
  deleteTripPlace,
  deleteTripPointOfInterest,
  deleteTripRestaurant,
  listBoardingPasses,
  listTripClimate,
  listTripEvents,
  listTripExperiences,
  listTripHotels,
  listTripItinerary,
  listTripPhotos,
  listTripPlaces,
  listTripPointsOfInterest,
  listTripRestaurants,
  saveBoardingPass,
  saveTripClimateDay,
  saveTripEvent,
  saveTripExperience,
  saveTripHotel,
  saveTripItineraryItem,
  saveTripPlace,
  saveTripPointOfInterest,
  saveTripRestaurant,
  type TripPhotoItem,
  type TripSummary
} from "./tripStore";

export type ItinerarySourceKind =
  | "manual"
  | "transfer"
  | "hotel"
  | "restaurant"
  | "city"
  | "poi"
  | "experience"
  | "event"
  | "boardingPass"
  | "climate";

export interface UnifiedItineraryEntry {
  id: string;
  sourceKind: ItinerarySourceKind;
  sourceId: string;
  title: string;
  date: string;
  endDate: string;
  startTime: string;
  endTime: string;
  city: string;
  address: string;
  notes: string;
  latitude?: number;
  longitude?: number;
  endLatitude?: number;
  endLongitude?: number;
  endCity?: string;
  endAddress?: string;
  transportModeId?: string;
  icon: string;
  sortOrder: number;
  photos: TripPhotoItem[];
}

export interface UnifiedItineraryDay {
  dayNumber: number;
  date: string;
  entries: UnifiedItineraryEntry[];
  photos: TripPhotoItem[];
}

export interface ItineraryEntryDraft {
  sourceKind: Exclude<ItinerarySourceKind, "boardingPass">;
  title: string;
  date: string;
  endDate: string;
  startTime: string;
  endTime: string;
  city: string;
  address: string;
  endCity: string;
  endAddress: string;
  latitude?: number;
  longitude?: number;
  endLatitude?: number;
  endLongitude?: number;
  transportModeId: string;
  notes: string;
}

const ICONS: Record<ItinerarySourceKind, string> = {
  manual: "•",
  transfer: "✈️",
  hotel: "🏨",
  restaurant: "🍽️",
  city: "📍",
  poi: "🏛️",
  experience: "✨",
  event: "🎟️",
  boardingPass: "✈️",
  climate: "🌤️"
};

const ORDER: Record<ItinerarySourceKind, number> = {
  boardingPass: 10,
  transfer: 12,
  hotel: 20,
  city: 30,
  poi: 40,
  experience: 50,
  event: 55,
  restaurant: 60,
  climate: 80,
  manual: 70
};

function parseDate(value: string): number {
  const time = Date.parse(`${value}T00:00:00Z`);
  return Number.isFinite(time) ? time : Number.NaN;
}

export function tripDateRange(trip: Pick<TripSummary, "startDate" | "endDate">, maxDays = 180): string[] {
  const start = parseDate(trip.startDate);
  const end = parseDate(trip.endDate);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) return [];
  const result: string[] = [];
  for (let time = start; time <= end && result.length < maxDays; time += 86400000) {
    result.push(new Date(time).toISOString().slice(0, 10));
  }
  return result;
}

/** An explicitly selected daily city wins over general trip destination or activity locations. */
export function resolveTripDayCity(uid: string, trip: TripSummary, date: string): string {
  const matches = listTripPlaces(uid, trip.id).filter(item =>
    Boolean(item.startDate) && item.startDate <= date && (item.endDate || item.startDate) >= date
  );
  const exact = matches.find(item => item.startDate === date && (!item.endDate || item.endDate === date));
  const selected = exact || matches[0];
  return (selected?.city || selected?.name || trip.destination || "").trim();
}

export function nextTripDate(date: string): string {
  const time = parseDate(date);
  if (!Number.isFinite(time)) return date;
  return new Date(time + 86400000).toISOString().slice(0, 10);
}

function dateInRange(date: string, start: string, end: string, endExclusive = false): boolean {
  if (!date || !start) return false;
  if (!end) return date === start;
  return endExclusive ? date >= start && date < end : date >= start && date <= end;
}

function normalize(value: string): string {
  return String(value || "").trim().toLocaleLowerCase().replace(/[^a-z0-9à-ÿ]+/gi, " ").replace(/\s+/g, " ").trim();
}

function photoMatches(entry: Omit<UnifiedItineraryEntry, "photos">, photo: TripPhotoItem): boolean {
  if (photo.sourceId && photo.sourceId === entry.sourceId) return true;
  if (photo.date && photo.date !== entry.date) return false;
  if (photo.sourceKind && photo.sourceKind === entry.sourceKind && !photo.sourceId) return true;
  const photoCity = normalize(photo.city);
  if (!photoCity) return false;
  return [entry.city, entry.address, entry.title, entry.endCity || ""]
    .map(normalize)
    .some((value) => value && (value.includes(photoCity) || photoCity.includes(value)));
}

function makeEntry(
  value: Omit<UnifiedItineraryEntry, "photos">,
  photos: TripPhotoItem[]
): UnifiedItineraryEntry {
  return { ...value, photos: photos.filter((photo) => photo.url && photoMatches(value, photo)) };
}

function safeNumber(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}

export function buildUnifiedItinerary(uid: string, trip: TripSummary): UnifiedItineraryDay[] {
  const dates = tripDateRange(trip);
  const photos = listTripPhotos(uid, trip.id).filter((photo) => photo.status === "uploaded" && Boolean(photo.url));
  const entries: UnifiedItineraryEntry[] = [];

  for (const item of listTripItinerary(uid, trip.id)) {
    if (!item.date) continue;
    entries.push(makeEntry({
      id: `manual:${item.id}`,
      sourceKind: item.type === "transfer" ? "transfer" : "manual",
      sourceId: item.id,
      title: item.title,
      date: item.date,
      endDate: item.endDate || item.date,
      startTime: item.startTime,
      endTime: item.endTime,
      city: item.city,
      address: item.address,
      endCity: item.endCity || "",
      endAddress: item.endAddress || "",
      transportModeId: item.transportModeId || "",
      notes: item.notes,
      latitude: safeNumber(item.latitude),
      longitude: safeNumber(item.longitude),
      endLatitude: safeNumber(item.endLatitude),
      endLongitude: safeNumber(item.endLongitude),
      icon: item.type === "transfer" ? ICONS.transfer : ICONS.manual,
      sortOrder: item.type === "transfer" ? ORDER.transfer : ORDER.manual
    }, photos));
  }

  for (const item of listBoardingPasses(uid, trip.id)) {
    if (!item.departureDate) continue;
    const origin = item.originCity || item.originCode;
    const destination = item.destinationCity || item.destinationCode;
    entries.push(makeEntry({
      id: `boardingPass:${item.id}`,
      sourceKind: "boardingPass",
      sourceId: item.id,
      title: [origin, destination].filter(Boolean).join(" → ") || item.flightNumber || item.airline,
      date: item.departureDate,
      endDate: item.departureDate,
      startTime: item.departureTime,
      endTime: item.arrivalTime,
      city: origin,
      address: origin,
      endCity: destination,
      endAddress: destination,
      notes: [item.airline, item.flightNumber, item.terminal ? `T ${item.terminal}` : "", item.gate ? `G ${item.gate}` : "", item.seat ? `#${item.seat}` : ""].filter(Boolean).join(" · "),
      icon: ICONS.boardingPass,
      sortOrder: ORDER.boardingPass
    }, photos));
  }

  for (const hotel of listTripHotels(uid, trip.id)) {
    for (const date of dates.filter((day) => dateInRange(day, hotel.checkIn, hotel.checkOut, Boolean(hotel.checkOut && hotel.checkOut !== hotel.checkIn)))) {
      entries.push(makeEntry({
        id: `hotel:${hotel.id}:${date}`,
        sourceKind: "hotel",
        sourceId: hotel.id,
        title: hotel.name,
        date,
        endDate: date,
        startTime: "",
        endTime: "",
        city: hotel.city,
        address: hotel.address,
        notes: hotel.notes,
        latitude: safeNumber(hotel.latitude),
        longitude: safeNumber(hotel.longitude),
        icon: ICONS.hotel,
        sortOrder: ORDER.hotel
      }, photos));
    }
  }

  for (const item of listTripRestaurants(uid, trip.id)) {
    if (!item.date) continue;
    entries.push(makeEntry({
      id: `restaurant:${item.id}`,
      sourceKind: "restaurant",
      sourceId: item.id,
      title: item.name,
      date: item.date,
      endDate: item.date,
      startTime: item.time,
      endTime: "",
      city: item.city,
      address: item.address,
      notes: item.notes,
      latitude: safeNumber(item.latitude),
      longitude: safeNumber(item.longitude),
      icon: ICONS.restaurant,
      sortOrder: ORDER.restaurant
    }, photos));
  }

  for (const place of listTripPlaces(uid, trip.id)) {
    const activeDates = dates.filter((date) => dateInRange(date, place.startDate, place.endDate || place.startDate));
    for (const date of activeDates) {
      entries.push(makeEntry({
        id: `city:${place.id}:${date}`,
        sourceKind: "city",
        sourceId: place.id,
        title: place.name,
        date,
        endDate: date,
        startTime: "",
        endTime: "",
        city: place.city || place.name,
        address: place.address,
        notes: place.notes,
        latitude: safeNumber(place.latitude),
        longitude: safeNumber(place.longitude),
        icon: ICONS.city,
        sortOrder: ORDER.city
      }, photos));
    }
  }

  for (const item of listTripPointsOfInterest(uid, trip.id)) {
    if (!item.visitDate) continue;
    entries.push(makeEntry({
      id: `poi:${item.id}`,
      sourceKind: "poi",
      sourceId: item.id,
      title: item.name,
      date: item.visitDate,
      endDate: item.visitDate,
      startTime: "",
      endTime: "",
      city: item.city,
      address: item.address,
      notes: item.notes,
      latitude: safeNumber(item.latitude),
      longitude: safeNumber(item.longitude),
      icon: ICONS.poi,
      sortOrder: ORDER.poi
    }, photos));
  }

  for (const item of listTripExperiences(uid, trip.id)) {
    if (!item.date) continue;
    entries.push(makeEntry({
      id: `experience:${item.id}`,
      sourceKind: "experience",
      sourceId: item.id,
      title: item.title,
      date: item.date,
      endDate: item.date,
      startTime: item.time,
      endTime: "",
      city: item.city,
      address: item.address || item.placeName,
      notes: item.notes,
      latitude: safeNumber(item.latitude),
      longitude: safeNumber(item.longitude),
      icon: ICONS.experience,
      sortOrder: ORDER.experience
    }, photos));
  }

  for (const item of listTripEvents(uid, trip.id)) {
    if (!item.date) continue;
    entries.push(makeEntry({
      id: `event:${item.id}`,
      sourceKind: "event",
      sourceId: item.id,
      title: item.title,
      date: item.date,
      endDate: item.date,
      startTime: item.startTime,
      endTime: item.endTime,
      city: item.city,
      address: item.address || item.venue,
      notes: item.notes,
      latitude: safeNumber(item.latitude),
      longitude: safeNumber(item.longitude),
      icon: ICONS.event,
      sortOrder: ORDER.event
    }, photos));
  }

  for (const item of listTripClimate(uid, trip.id)) {
    entries.push(makeEntry({
      id: `climate:${item.id}`,
      sourceKind: "climate",
      sourceId: item.id,
      title: item.conditions || item.summary || "Climate",
      date: item.date,
      endDate: item.date,
      startTime: "",
      endTime: "",
      city: String(item.summary || "").replace(/^(Località|Location|Ubicación|Localización)\s*:\s*/i, "").split(",")[0].trim(),
      address: "",
      notes: [item.minTemp || item.maxTemp ? `${item.minTemp || "–"} / ${item.maxTemp || "–"}` : ""].filter(Boolean).join(" · "),
      icon: ICONS.climate,
      sortOrder: ORDER.climate
    }, photos));
  }

  return dates.map((date, index) => ({
    dayNumber: index + 1,
    date,
    entries: entries
      .filter((entry) => entry.date === date)
      .sort((a, b) => {
        const timeA = a.startTime || "99:99";
        const timeB = b.startTime || "99:99";
        if (timeA !== timeB) return timeA.localeCompare(timeB);
        if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
        return a.title.localeCompare(b.title);
      }),
    photos: photos.filter((photo) => photo.date === date)
  }));
}

export function listItineraryLinkTargets(uid: string, trip: TripSummary): UnifiedItineraryEntry[] {
  return buildUnifiedItinerary(uid, trip).flatMap((day) => day.entries).filter((entry) => entry.sourceKind !== "climate");
}

function findById<T extends { id: string }>(items: T[], id: string): T | undefined {
  return items.find((item) => item.id === id);
}

export function updateUnifiedItineraryEntry(uid: string, trip: TripSummary, entry: UnifiedItineraryEntry, draft: Omit<ItineraryEntryDraft, "sourceKind">): void {
  if (entry.sourceKind === "hotel") {
    const item = findById(listTripHotels(uid, trip.id), entry.sourceId);
    if (!item) return;
    saveTripHotel(uid, trip.id, { ...item, name: draft.title.trim(), city: draft.city.trim(), address: draft.address.trim(), checkIn: draft.date, checkOut: draft.endDate || nextTripDate(draft.date), notes: draft.notes.trim(), latitude: draft.latitude ?? item.latitude, longitude: draft.longitude ?? item.longitude }, item.id);
    return;
  }
  if (entry.sourceKind === "restaurant") {
    const item = findById(listTripRestaurants(uid, trip.id), entry.sourceId);
    if (!item) return;
    saveTripRestaurant(uid, trip.id, { ...item, name: draft.title.trim(), city: draft.city.trim(), address: draft.address.trim(), date: draft.date, time: draft.startTime, notes: draft.notes.trim(), latitude: draft.latitude ?? item.latitude, longitude: draft.longitude ?? item.longitude }, item.id);
    return;
  }
  if (entry.sourceKind === "city") {
    const item = findById(listTripPlaces(uid, trip.id), entry.sourceId);
    if (!item) return;
    saveTripPlace(uid, trip.id, { ...item, name: draft.title.trim(), city: draft.city.trim() || draft.title.trim(), address: draft.address.trim(), startDate: draft.date, endDate: draft.endDate || draft.date, notes: draft.notes.trim(), latitude: draft.latitude ?? item.latitude, longitude: draft.longitude ?? item.longitude }, item.id);
    return;
  }
  if (entry.sourceKind === "poi") {
    const item = findById(listTripPointsOfInterest(uid, trip.id), entry.sourceId);
    if (!item) return;
    saveTripPointOfInterest(uid, trip.id, { ...item, name: draft.title.trim(), city: draft.city.trim(), address: draft.address.trim(), visitDate: draft.date, notes: draft.notes.trim(), latitude: draft.latitude ?? item.latitude, longitude: draft.longitude ?? item.longitude }, item.id);
    return;
  }
  if (entry.sourceKind === "experience") {
    const item = findById(listTripExperiences(uid, trip.id), entry.sourceId);
    if (!item) return;
    saveTripExperience(uid, trip.id, { ...item, title: draft.title.trim(), city: draft.city.trim(), address: draft.address.trim(), date: draft.date, time: draft.startTime, notes: draft.notes.trim(), latitude: draft.latitude ?? item.latitude, longitude: draft.longitude ?? item.longitude }, item.id);
    return;
  }
  if (entry.sourceKind === "event") {
    const item = findById(listTripEvents(uid, trip.id), entry.sourceId);
    if (!item) return;
    saveTripEvent(uid, trip.id, { ...item, title: draft.title.trim(), city: draft.city.trim(), address: draft.address.trim(), date: draft.date, startTime: draft.startTime, endTime: draft.endTime, notes: draft.notes.trim(), latitude: draft.latitude ?? item.latitude, longitude: draft.longitude ?? item.longitude }, item.id);
    return;
  }
  if (entry.sourceKind === "boardingPass") {
    const item = findById(listBoardingPasses(uid, trip.id), entry.sourceId);
    if (!item) return;
    const parts = draft.title.split(/\s*[→>-]\s*/).map((value) => value.trim()).filter(Boolean);
    saveBoardingPass(uid, trip.id, {
      ...item,
      originCity: draft.city.trim() || parts[0] || item.originCity,
      destinationCity: parts[1] || item.destinationCity,
      departureDate: draft.date,
      departureTime: draft.startTime,
      arrivalTime: draft.endTime
    }, item.id);
    return;
  }
  if (entry.sourceKind === "climate") {
    const item = findById(listTripClimate(uid, trip.id), entry.sourceId);
    if (!item) return;
    saveTripClimateDay(uid, trip.id, { ...item, date: draft.date, conditions: draft.title.trim(), summary: draft.notes.trim() }, item.id);
    return;
  }
  const item = findById(listTripItinerary(uid, trip.id), entry.sourceId);
  if (!item) return;
  saveTripItineraryItem(uid, trip.id, {
    ...item,
    title: draft.title.trim(),
    date: draft.date,
    endDate: draft.endDate || draft.date,
    startTime: draft.startTime,
    endTime: draft.endTime,
    city: draft.city.trim(),
    address: draft.address.trim(),
    endCity: draft.endCity.trim(),
    endAddress: draft.endAddress.trim(),
    latitude: draft.latitude ?? item.latitude,
    longitude: draft.longitude ?? item.longitude,
    endLatitude: draft.endLatitude ?? item.endLatitude,
    endLongitude: draft.endLongitude ?? item.endLongitude,
    transportModeId: draft.transportModeId || item.transportModeId || "",
    notes: draft.notes.trim(),
    type: entry.sourceKind === "transfer" ? "transfer" : item.type
  }, item.id);
}

export function createUnifiedItineraryEntry(uid: string, trip: TripSummary, draft: ItineraryEntryDraft): void {
  const title = draft.title.trim();
  if (!title || !draft.date) return;

  if (draft.sourceKind === "hotel") {
    saveTripHotel(uid, trip.id, {
      name: title, city: draft.city.trim(), address: draft.address.trim(), checkIn: draft.date, checkOut: draft.endDate || nextTripDate(draft.date),
      bookingReference: "", bookedWith: "", bookedWithOther: "", notes: draft.notes.trim(), latitude: draft.latitude, longitude: draft.longitude
    });
    return;
  }
  if (draft.sourceKind === "restaurant") {
    saveTripRestaurant(uid, trip.id, {
      name: title, city: draft.city.trim(), address: draft.address.trim(), date: draft.date, time: draft.startTime,
      reservationWith: "", reservationWithOther: "", bookingReference: "", notes: draft.notes.trim(), latitude: draft.latitude, longitude: draft.longitude
    });
    return;
  }
  if (draft.sourceKind === "city") {
    saveTripPlace(uid, trip.id, {
      name: title, city: draft.city.trim() || title, address: draft.address.trim(), startDate: draft.date, endDate: draft.endDate || draft.date,
      notes: draft.notes.trim(), photoIndex: 0, latitude: draft.latitude, longitude: draft.longitude
    });
    return;
  }
  if (draft.sourceKind === "poi") {
    saveTripPointOfInterest(uid, trip.id, {
      cityId: "", name: title, city: draft.city.trim(), address: draft.address.trim(), notes: draft.notes.trim(), visitDate: draft.date, research: "",
      latitude: draft.latitude, longitude: draft.longitude
    });
    return;
  }
  if (draft.sourceKind === "experience") {
    saveTripExperience(uid, trip.id, {
      title, placeName: title, city: draft.city.trim(), address: draft.address.trim(), date: draft.date, time: draft.startTime,
      price: null, currency: "EUR", notes: draft.notes.trim(), latitude: draft.latitude, longitude: draft.longitude
    });
    return;
  }
  if (draft.sourceKind === "event") {
    saveTripEvent(uid, trip.id, {
      title, venue: title, city: draft.city.trim(), address: draft.address.trim(), date: draft.date,
      startTime: draft.startTime, endTime: draft.endTime, category: "", price: null, currency: "EUR", url: "", notes: draft.notes.trim(),
      latitude: draft.latitude, longitude: draft.longitude
    });
    return;
  }
  if (draft.sourceKind === "climate") {
    saveTripClimateDay(uid, trip.id, {
      date: draft.date, conditions: title, minTemp: "", maxTemp: "", summary: draft.notes.trim(), notes: ""
    });
    return;
  }

  saveTripItineraryItem(uid, trip.id, {
    title,
    date: draft.date,
    endDate: draft.endDate || draft.date,
    startTime: draft.startTime,
    endTime: draft.endTime,
    city: draft.city.trim(),
    address: draft.address.trim(),
    endCity: draft.endCity.trim(),
    endAddress: draft.endAddress.trim(),
    latitude: draft.latitude,
    longitude: draft.longitude,
    endLatitude: draft.endLatitude,
    endLongitude: draft.endLongitude,
    transportModeId: draft.transportModeId,
    type: draft.sourceKind === "transfer" ? "transfer" : "activity",
    notes: draft.notes.trim()
  });
}

export function itinerarySourceLabelKey(sourceKind: ItinerarySourceKind): string {
  return `itinerary.source.${sourceKind}`;
}

export function deleteUnifiedItineraryEntry(uid: string, trip: TripSummary, entry: UnifiedItineraryEntry): void {
  if (entry.sourceKind === "hotel") { deleteTripHotel(uid, trip.id, entry.sourceId); return; }
  if (entry.sourceKind === "restaurant") { deleteTripRestaurant(uid, trip.id, entry.sourceId); return; }
  if (entry.sourceKind === "city") { deleteTripPlace(uid, trip.id, entry.sourceId); return; }
  if (entry.sourceKind === "poi") { deleteTripPointOfInterest(uid, trip.id, entry.sourceId); return; }
  if (entry.sourceKind === "experience") { deleteTripExperience(uid, trip.id, entry.sourceId); return; }
  if (entry.sourceKind === "event") { deleteTripEvent(uid, trip.id, entry.sourceId); return; }
  if (entry.sourceKind === "boardingPass") { deleteBoardingPass(uid, trip.id, entry.sourceId); return; }
  if (entry.sourceKind === "climate") { deleteTripClimateDay(uid, trip.id, entry.sourceId); return; }
  deleteTripItineraryItem(uid, trip.id, entry.sourceId);
}
